import { useState, useEffect, useCallback } from "react";
import { volunteerService } from "../../../../services/api/volunteerService";
import type { Volunteer } from "../../../../types/volunteer";
import { useAuth } from "../../../../contexts/AuthContext";
import {jwtDecode} from "jwt-decode";
import { toast } from "react-toastify";

interface DecodedToken {
  exp: number;
  // Add other token fields if needed
}

export const useVolunteer = () => {
  const { tokens, signOut } = useAuth();

  // States
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
  const [volunteerDetails, setVolunteerDetails] = useState<Volunteer | null>(null);

  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const decoded: DecodedToken = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true; // If token is invalid, treat it as expired
    }
  }, []);

  const loadVolunteers = useCallback(async () => {
    if (!tokens?.idToken) {
      setError("No authentication token found. Please log in again.");
      return;
    }

    if (isTokenExpired(tokens.idToken)) {
      setError("Session expired. Please log in again.");
      signOut();
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await volunteerService.getVolunteers(tokens.accessToken);
      console.log("Volunteers:", data);

      // Handle different response formats
      if (Array.isArray(data)) {
        setVolunteers(data);
      } else if (data && 'volunteers' in data && Array.isArray(data.volunteers)) {
        setVolunteers(data.volunteers);
      } else {
        console.warn("Unexpected volunteer data format:", data);
        throw new Error("Invalid response format from server");
      }
    } catch (err: any) {
      console.error("Failed to load volunteers:", err);
      setError(err.message || "Unable to load volunteers. Please try again later.");
      toast.error(err.message || "Unable to load volunteers. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [tokens, isTokenExpired, signOut]);

  const handleViewVolunteer = useCallback(
    async (volunteerId: string) => {
      if (!tokens?.idToken) {
        setError("No authentication token found. Please log in again.");
        return;
      }

      if (isTokenExpired(tokens.idToken)) {
        setError("Session expired. Please log in again.");
        signOut();
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const volunteer = await volunteerService.getVolunteerById(tokens.idToken, volunteerId);
        setVolunteerDetails(volunteer);
        setSelectedVolunteer(volunteer);
      } catch (err: any) {
        console.error("Failed to load volunteer details:", err);
        setError(err.message || "Unable to load volunteer details.");
        toast.error(err.message || "Unable to load volunteer details.");
      } finally {
        setLoading(false);
      }
    },
    [tokens, isTokenExpired, signOut]
  );

  const handleUpdateVolunteer = useCallback(
    async (volunteerId: string, updates: Partial<Volunteer>) => {
      if (!tokens?.idToken) {
        setError("No authentication token found. Please log in again.");
        return;
      }

      if (isTokenExpired(tokens.idToken)) {
        setError("Session expired. Please log in again.");
        signOut();
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const updatedVolunteer = await volunteerService.updateVolunteer(tokens.idToken, volunteerId, updates);
        setVolunteers((prevVolunteers) =>
          prevVolunteers.map((vol) => (vol.id === volunteerId ? updatedVolunteer : vol))
        );
        setSuccess("Volunteer updated successfully.");
        toast.success("Volunteer updated successfully.");
      } catch (err: any) {
        console.error("Failed to update volunteer:", err);
        setError(err.message || "Unable to update volunteer.");
        toast.error(err.message || "Unable to update volunteer.");
      } finally {
        setLoading(false);
      }
    },
    [tokens, isTokenExpired, signOut]
  );

  const handleDeleteVolunteer = useCallback(
    async (volunteerId: string) => {
      if (!tokens?.idToken) {
        setError("No authentication token found. Please log in again.");
        return;
      }

      if (isTokenExpired(tokens.idToken)) {
        setError("Session expired. Please log in again.");
        signOut();
        return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        await volunteerService.deleteVolunteer(tokens.idToken, volunteerId);
        setVolunteers((prevVolunteers) => prevVolunteers.filter((vol) => vol.id !== volunteerId));
        setSuccess("Volunteer deleted successfully.");
        toast.success("Volunteer deleted successfully.");
      } catch (err: any) {
        console.error("Failed to delete volunteer:", err);
        setError(err.message || "Unable to delete volunteer.");
        toast.error(err.message || "Unable to delete volunteer.");
      } finally {
        setLoading(false);
      }
    },
    [tokens, isTokenExpired, signOut]
  );

  useEffect(() => {
    loadVolunteers();
  }, [loadVolunteers]);

  return {
    volunteers,
    loading,
    error,
    success,
    selectedVolunteer,
    volunteerDetails,
    setSelectedVolunteer,
    loadVolunteers,
    handleViewVolunteer,
    handleUpdateVolunteer,
    handleDeleteVolunteer,
  };
};